import React, { useState, useRef, useEffect } from "react";
import InputMask from "react-input-mask";
import { ReactComponent as HidePwDefault } from "../../styles/default/assets/icons/hide-pw-default.svg";
import themePropTypes from "../theme-prop-types";
import validIcon from "../../styles/default/assets/icons/valid-icon.svg";
import invalidIcon from "../../styles/default/assets/icons/invalid-icon.svg";
import eyeIcon from "../../styles/default/assets/icons/icon-eye.svg";
import eyeCrossedIcon from "../../styles/default/assets/icons/icon-eye-crossed.svg";
import { MAIN_PASSWORD_PATTERN } from "../../utils/validators";

const ShowHideButton = ({
  hidden,
  toggleHidden,
  onShowHideEnterDown,
  className,
  translations,
  isUnifiedRegistration
}) => (
  // eslint-disable-next-line
  <span
    className={className}
    onClick={toggleHidden}
    onKeyDown={onShowHideEnterDown}
    role='button'
    tabIndex='-1'
  >
    {isUnifiedRegistration ? (
      <img
        className='show-hide-icon'
        src={hidden ? eyeIcon : eyeCrossedIcon}
        alt={hidden ? translations.show_value : translations.hide_value}
      />
    ) : hidden ? (
      translations.show_uc
    ) : (
      translations.hide_uc
    )}
  </span>
);

const PasswordStrength = ({ name, password, data, translations }) => {
  const { regex_validation: regexValidation } = data;

  const passwordConditionCheck = regExp => !!password.match(regExp);

  return (
    <>
      {password && !passwordConditionCheck(MAIN_PASSWORD_PATTERN) && (
        <ul
          id={`inline-error-${name}`}
          className='password-strength-box'
          aria-label={translations?.password_strength}
        >
          {regexValidation.map(({ error, pattern }) => {
            const isValid = passwordConditionCheck(pattern);

            return (
              <li className='password-item' key={error}>
                <img
                  className='validation-check-icon'
                  src={isValid ? validIcon : invalidIcon}
                  alt={
                    isValid
                      ? translations?.password_strength_passed
                      : translations?.password_strength_failed
                  }
                />
                <span className={isValid ? "valid" : "invalid"}>{error}</span>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
};

const TextField = ({
  maskingAttributes,
  fieldAttributes: outerFieldAttributes,
  placeholder,
  updateValue,
  onEnterDown,
  name,
  id,
  labelId,
  invalid,
  disabled = false,
  params = "",
  data,
  translations,
  passwordValue,
  isUnifiedRegistration,
  onChangeValue = null
}) => {
  const showHideIcon = params.includes("TDFieldOptionShowHidePassword");
  const [passwordInput, setPasswordInput] = useState(
    showHideIcon || !!params.includes("TDFieldOptionShowPasswordButton")
  );

  const props = {};
  if (placeholder && placeholder !== "%") {
    props.placeholder = placeholder;
  }

  const [hidden, setHidden] = useState(true);
  const [password, setPassword] = useState("");

  const inputRef = useRef();

  useEffect(() => {
    if (passwordValue) {
      setPassword(passwordValue);
    }
  }, []);

  const fieldAttributes = {
    ...outerFieldAttributes,
    type: !params.includes("TDFieldOptionPassword")
      ? outerFieldAttributes.type
      : hidden
      ? "password"
      : "text",
    value: params.includes("TDFieldOptionPassword")
      ? password
      : outerFieldAttributes.value,
    placeholder: placeholder !== "%" ? placeholder : "",
    mask: outerFieldAttributes.value ? maskingAttributes?.mask : "",
    maskChar: maskingAttributes?.maskChar
  };

  const isPercentage = placeholder === "%";
  const isDisabled = disabled || params.includes("TDFieldOptionDisabled");
  const pdLight700 = "#565757";

  const togglePasswordInput = () => setPasswordInput(!passwordInput);

  const HideIcon = () => (
    <HidePwDefault
      role='img'
      style={{ position: "absolute", right: 22 }}
      width={16}
      height={16}
      fill={pdLight700}
      onClick={togglePasswordInput}
    />
  );

  const ariaDescribedby = isUnifiedRegistration
    ? invalid
      ? `inline-error-${name}`
      : null
    : `${name}-helper-text`;

  const showHideButtonAttributes = {
    renderButton: !!params.includes("TDFieldOptionShowPasswordButton"),
    class:
      password === "" ? "button-show-password-hidden" : "button-show-password"
  };

  const onShowHideEnterDown = event => {
    if (event.keyCode === 13) {
      event.preventDefault();
      event.stopPropagation();
      setHidden(!hidden);
      inputRef.current.focus();
    }
  };

  const onChange = e => {
    setPassword(e.target.value);
    if (e.target.value === "") {
      setHidden(true);
    }
    updateValue(e);
    if (onChangeValue) {
      onChangeValue(e);
    }
  };

  return (
    <>
      <span
        className={`websdk-relative ${
          isPercentage || isDisabled || showHideIcon
            ? "websdk-flex websdk-items-center"
            : ""
        }`}
      >
        <InputMask
          className={`input-web-sdk ${invalid ? "invalid" : ""}`}
          data-testid={`input-${name}`}
          id={id || `${name}-input`}
          {...fieldAttributes}
          mask={maskingAttributes?.mask}
          maskChar={maskingAttributes?.maskChar}
          placeholder={maskingAttributes?.placeholder}
          onBlur={updateValue}
          onKeyDown={onEnterDown}
          onChange={onChange}
          disabled={isDisabled}
          type={passwordInput ? "password" : "text"}
          aria-labelledby={labelId || `${name}-label`}
          aria-invalid={invalid}
          aria-describedby={ariaDescribedby}
          inputRef={inputRef}
          {...props}
        />
        {isPercentage && (
          <span className='websdk-self-center websdk-ml-2'>%</span>
        )}
        {showHideIcon && !isDisabled && <HideIcon />}
        {showHideButtonAttributes.renderButton && (
          <ShowHideButton
            hidden={hidden}
            toggleHidden={() => {
              setHidden(!hidden);
              togglePasswordInput();
            }}
            onShowHideEnterDown={onShowHideEnterDown}
            className={showHideButtonAttributes.class}
            isUnifiedRegistration={isUnifiedRegistration}
            translations={translations}
          />
        )}
      </span>
      {isUnifiedRegistration &&
        params.includes("TDFieldOptionPasswordRules") && (
          <PasswordStrength
            name={name}
            password={password}
            data={data}
            translations={translations}
          />
        )}
    </>
  );
};

TextField.propTypes = themePropTypes.textField;
ShowHideButton.propTypes = themePropTypes.showHideButton;
PasswordStrength.propTypes = themePropTypes.passwordStrength;

export default TextField;
